<template>
  <div class="share-poster">
    <div v-if="posterUrl" class="poster-box" @click.stop>
      <img class="poster-close" src="@/assets/images/home/ic_fork@2x.png" alt="关闭" @click="handleClose" />
      <div class="poster-scroll">
        <img class="poster-img" :src="posterUrl" :alt="posterFileName" @contextmenu="savePoster" />
      </div>
      <div class="poster-tip">请长按保存图片到手机后转发</div>
    </div>
  </div>
</template>

<script>
import qrcode from 'qrcode-generator'
import shareLogo from '@/assets/images/home/logo@2x.png'

// 二维码宽为整张卡片宽度的 46%，画在分享图下方，不和分享图重叠
const QR_WIDTH_RATIO = 0.46
// 海报宽度固定。封面高度按原图比例计算，不裁切
const CARD_WIDTH = 750
// 移动端画布过高会绘制失败，超出时整张等比缩小，内容仍完整
const MAX_CANVAS_HEIGHT = 8192

export default {
  name: 'LiveSharePoster',
  props: {
    background: {
      type: String,
      required: true
    },
    liveId: {
      type: [String, Number],
      required: true
    },
    shareUserId: {
      type: [String, Number],
      required: true
    },
    liveTitle: {
      type: String,
      default: ''
    }
  },
  computed: {
    // 画布导出的是 data URL，没有文件名，浏览器长按会默认存成「下载.png」
    posterFileName() {
      const title = String(this.liveTitle || '分享海报').replace(/[\\/:*?"<>|\r\n]/g, '').trim()
      return `${title || '分享海报'}.png`
    }
  },
  data() {
    return {
      posterUrl: ''
    }
  },
  mounted() {
    // 详情页正文里有大图。微信长按会优先保存这张网络图，而不是弹窗里的合成海报
    this.suppressPageImages()
    this.createPoster()
  },
  beforeDestroy() {
    this.restorePageImages()
  },
  methods: {
    suppressPageImages() {
      this.pageImages = []
      Array.from(document.querySelectorAll('.my-live img')).forEach(img => {
        if (this.$el.contains(img)) return
        this.pageImages.push({
          el: img,
          src: img.getAttribute('src'),
          visibility: img.style.visibility
        })
        img.style.visibility = 'hidden'
        if (img.closest('.live-box')) {
          img.setAttribute('src', '')
        }
      })
    },
    restorePageImages() {
      (this.pageImages || []).forEach(item => {
        item.el.style.visibility = item.visibility
        if (item.src) item.el.setAttribute('src', item.src)
      })
      this.pageImages = []
    },
    handleClose() {
      this.$emit('close')
    },
    savePoster(event) {
      if (!this.posterUrl) return
      // 微信自带的「保存图片」不走这里，避免把系统菜单拦掉
      if (/MicroMessenger/i.test(navigator.userAgent)) return
      if (event && event.preventDefault) event.preventDefault()
      const link = document.createElement('a')
      link.href = this.posterUrl
      link.download = this.posterFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    // 本地 localhost 画布导出不能直接用测试环境图片，会因缺少跨域响应头加载失败。
    // 开发代理已把 /bw 转到 cartojourneyqa，这里改成同源路径后再绘制。
    resolveImageUrl(src) {
      if (!src || process.env.NODE_ENV !== 'development') return src
      try {
        const url = new URL(src, window.location.origin)
        if (url.origin === window.location.origin) return src
        if (url.pathname.indexOf('/bw/') === 0) {
          return `${url.pathname}${url.search}`
        }
      } catch (error) {
        return src
      }
      return src
    },
    loadImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        const imageUrl = this.resolveImageUrl(src)
        let imageOrigin = ''
        try {
          imageOrigin = new URL(imageUrl, window.location.origin).origin
        } catch (error) {
          imageOrigin = ''
        }
        // 仅跨域图片需要匿名跨域；同源图片加这个头反而会触发 CORS 校验失败
        if (imageOrigin && imageOrigin !== window.location.origin) {
          image.crossOrigin = 'anonymous'
        }
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('image load fail'))
        image.src = imageUrl
      })
    },
    // 扫码打开本场直播详情，携带分享人、分享时间和渠道
    buildShareUrl() {
      const shareTime = Date.now()
      const query = [
        `liveId=${encodeURIComponent(this.liveId)}`,
        'entrance=180',
        'channel=180',
        `shareUserId=${encodeURIComponent(this.shareUserId)}`,
        `shareTime=${shareTime}`
      ].join('&')
      return `${window.location.origin}${process.env.BASE_URL}/liveDetails?${query}`
    },
    createQrCanvas(url, size) {
      // H 纠错，中间盖 logo 后仍可被微信识别
      const qr = qrcode(0, 'H')
      qr.addData(url)
      qr.make()
      const margin = 2
      const moduleCount = qr.getModuleCount()
      const cellSize = Math.max(2, Math.floor(size / (moduleCount + margin * 2)))
      const drawSize = (moduleCount + margin * 2) * cellSize
      const canvas = document.createElement('canvas')
      canvas.width = drawSize
      canvas.height = drawSize
      const context = canvas.getContext('2d')
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, drawSize, drawSize)
      context.fillStyle = '#000000'
      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (!qr.isDark(row, col)) continue
          context.fillRect((col + margin) * cellSize, (row + margin) * cellSize, cellSize, cellSize)
        }
      }
      return canvas
    },
    // 整张海报四个角都切圆。分享图自己的下沿仍贴着白底，不单独倒角
    clipCard(context, x, y, width, height, radius) {
      context.beginPath()
      context.moveTo(x + radius, y)
      context.arcTo(x + width, y, x + width, y + radius, radius)
      context.arcTo(x + width, y + height, x + width - radius, y + height, radius)
      context.arcTo(x, y + height, x, y + height - radius, radius)
      context.arcTo(x, y, x + radius, y, radius)
      context.closePath()
      context.clip()
    },
    drawCoverImage(context, image, x, y, width, height, radius) {
      context.save()
      context.beginPath()
      context.moveTo(x, y + height)
      context.lineTo(x, y + radius)
      context.arcTo(x, y, x + radius, y, radius)
      context.lineTo(x + width - radius, y)
      context.arcTo(x + width, y, x + width, y + radius, radius)
      context.lineTo(x + width, y + height)
      context.closePath()
      context.clip()
      context.drawImage(image, x, y, width, height)
      context.restore()
    },
    posterLayout(background, cardWidth) {
      const padding = Math.round(cardWidth * 0.05)
      const bannerHeight = Math.max(1, Math.round(cardWidth * background.height / background.width))
      const qrSize = Math.round(cardWidth * QR_WIDTH_RATIO)
      const titleSize = Math.round(cardWidth * 0.048)
      const gap = Math.round(cardWidth * 0.05)
      const radius = Math.round(cardWidth * 0.04)
      const titleY = bannerHeight + gap + titleSize
      const qrY = titleY + gap
      const cardHeight = qrY + qrSize + padding
      return {cardWidth, padding, bannerHeight, qrSize, titleSize, gap, radius, titleY, qrY, cardHeight}
    },
    drawLogo(context, logo, qrX, qrY, qrSize) {
      const logoWidth = qrSize * 0.26
      const logoHeight = logoWidth * (logo.height / logo.width)
      const x = qrX + (qrSize - logoWidth) / 2
      const y = qrY + (qrSize - logoHeight) / 2
      const padding = qrSize * 0.02
      context.fillStyle = '#ffffff'
      context.fillRect(x - padding, y - padding, logoWidth + padding * 2, logoHeight + padding * 2)
      context.drawImage(logo, x, y, logoWidth, logoHeight)
    },
    async createPoster() {
      this.$toast.loading({
        message: '生成中...',
        forbidClick: true,
        duration: 0
      })
      try {
        const [background, logo] = await Promise.all([
          this.loadImage(this.background),
          this.loadImage(shareLogo)
        ])
        let layout = this.posterLayout(background, CARD_WIDTH)
        if (layout.cardHeight > MAX_CANVAS_HEIGHT) {
          layout = this.posterLayout(background, Math.max(320, Math.floor(CARD_WIDTH * MAX_CANVAS_HEIGHT / layout.cardHeight)))
        }
        const {cardWidth, bannerHeight, qrSize, titleSize, radius, titleY, qrY, cardHeight} = layout
        const qrCanvas = this.createQrCanvas(this.buildShareUrl(), qrSize)
        const canvas = document.createElement('canvas')
        canvas.width = cardWidth
        canvas.height = cardHeight
        const context = canvas.getContext('2d')
        context.save()
        this.clipCard(context, 0, 0, cardWidth, cardHeight, radius)
        context.fillStyle = '#ffffff'
        context.fillRect(0, bannerHeight, cardWidth, cardHeight - bannerHeight)
        this.drawCoverImage(context, background, 0, 0, cardWidth, bannerHeight, radius)
        context.textAlign = 'center'
        context.fillStyle = '#EB1700'
        context.font = `600 ${titleSize}px "Source Han Sans CN", "PingFang SC", "Microsoft YaHei", sans-serif`
        context.fillText('精彩内容  扫码观看', cardWidth / 2, titleY)
        const qrX = (cardWidth - qrSize) / 2
        context.imageSmoothingEnabled = false
        context.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize)
        context.imageSmoothingEnabled = true
        this.drawLogo(context, logo, qrX, qrY, qrSize)
        context.restore()
        // 合成成一张图再展示。微信长按只能保存 img，不能分别保存背景和二维码
        this.posterUrl = canvas.toDataURL('image/png')
        this.$toast.clear()
      } catch (error) {
        console.error(error)
        this.$toast.clear()
        this.$toast.fail('海报生成失败')
        this.handleClose()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.share-poster {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 9999998;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .poster-box {
    position: relative;
    width: 86vw;
    max-height: 86vh;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .poster-scroll {
    width: 100%;
    max-height: calc(86vh - 36px);
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 12px;
  }
  .poster-img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
    -webkit-touch-callout: default !important;
    -webkit-user-select: auto !important;
    user-select: auto !important;
  }
  .poster-tip {
    flex-shrink: 0;
    margin-top: 12px;
    font-size: 12px;
    line-height: 18px;
    color: #ffffff;
    text-align: center;
  }
  .poster-close {
    position: absolute;
    top: -36px;
    right: 0;
    width: 28px;
    height: 28px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/home/components/LiveSharePoster.vue